import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface Category {
  id: string;
  slug: string;
  name: string;
  icon: string;
  color: string;
  count: number;
  sort_order: number;
}

interface CategoryForm {
  slug: string;
  name: string;
  icon: string;
  color: string;
  count: string;
  sort_order: string;
}

const emptyForm: CategoryForm = { slug: '', name: '', icon: 'ri-store-line', color: 'from-orange-500 to-amber-500', count: '0', sort_order: '0' };

export default function CategoriesManager() {
  const [items, setItems] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CategoryForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase.from('categories').select('*').order('sort_order');
      if (fetchError) throw fetchError;
      setItems(data || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (c: Category) => {
    setEditingId(c.id);
    setForm({ slug: c.slug, name: c.name, icon: c.icon, color: c.color, count: String(c.count), sort_order: String(c.sort_order) });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this category?')) return;
    try {
      await supabase.from('categories').delete().eq('id', id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      slug: form.slug, name: form.name, icon: form.icon, color: form.color,
      count: parseInt(form.count) || 0, sort_order: parseInt(form.sort_order) || 0,
    };
    try {
      if (editingId) {
        await supabase.from('categories').update(payload).eq('id', editingId);
      } else {
        await supabase.from('categories').insert(payload);
      }
      setShowForm(false);
      fetchItems();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-20 text-center text-ink-500">Loading...</div>;
  if (error) return <div className="py-20 text-center"><p className="text-rose-600 text-sm mb-3">{error}</p><button onClick={fetchItems} className="h-9 px-4 rounded-lg bg-ink-900 text-white text-sm font-semibold whitespace-nowrap">Retry</button></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-bold text-ink-900">Categories ({items.length})</h2>
        <button onClick={openAdd} className="h-9 px-4 rounded-lg bg-ink-900 hover:bg-ink-800 text-white text-sm font-semibold whitespace-nowrap flex items-center gap-1.5 transition">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
          Add Category
        </button>
      </div>

      <div className="bg-white rounded-xl border border-ink-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="bg-ink-50/70 border-b border-ink-100">
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Category</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Slug</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Icon</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Count</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Order</th>
                <th className="text-right text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id} className="border-b border-ink-100 last:border-b-0 hover:bg-ink-50/50 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className={`w-9 h-9 rounded-lg bg-gradient-to-br ${c.color} text-white flex items-center justify-center`}>
                        <i className={`${c.icon} text-sm`}></i>
                      </span>
                      <span className="text-sm font-semibold text-ink-900">{c.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-ink-500">{c.slug}</td>
                  <td className="px-4 py-3 text-sm text-ink-500 font-mono">{c.icon}</td>
                  <td className="px-4 py-3 text-sm text-ink-700">{c.count}</td>
                  <td className="px-4 py-3 text-sm text-ink-700">{c.sort_order}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(c)} className="w-8 h-8 rounded-lg hover:bg-ink-100 text-ink-600 flex items-center justify-center transition"><i className="ri-edit-line text-sm"></i></button>
                      <button onClick={() => handleDelete(c.id)} className="w-8 h-8 rounded-lg hover:bg-rose-50 text-ink-600 hover:text-rose-600 flex items-center justify-center transition"><i className="ri-delete-bin-line text-sm"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
          <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm" onClick={() => setShowForm(false)}></div>
          <div className="relative bg-white rounded-2xl border border-ink-100 shadow-card w-full max-w-md">
            <div className="border-b border-ink-100 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-ink-900">{editingId ? 'Edit Category' : 'Add Category'}</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-lg hover:bg-ink-50 flex items-center justify-center text-ink-500 transition"><i className="ri-close-line"></i></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Slug</label>
                <input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Remix Icon Class</label>
                <input value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Gradient Class</label>
                <input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Count</label>
                  <input type="number" value={form.count} onChange={e => setForm({ ...form, count: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Sort Order</label>
                  <input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="h-9 px-4 rounded-lg border border-ink-200 text-sm font-semibold text-ink-600 hover:bg-ink-50 whitespace-nowrap transition">Cancel</button>
                <button type="submit" disabled={saving} className="h-9 px-4 rounded-lg bg-ink-900 hover:bg-ink-800 text-white text-sm font-semibold whitespace-nowrap transition disabled:opacity-50">{saving ? 'Saving...' : editingId ? 'Save' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}